/**
 * Prevents duplicate OAuth authorization-code exchanges.
 *
 * OAuth codes are single-use. If React remounts or an effect re-runs while `?code=`
 * is still in the URL, a second POST burns the code and providers return invalid_grant.
 *
 * @example
 * await exchangeOAuthCodeOnce("zoom", "abc123", async (code) => {
 *   const res = await fetch("/externalauth/zoom/callback", { body: JSON.stringify({ code }) });
 *   return res.json();
 * });
 * // A second call with the same provider+code awaits the first promise instead of re-posting.
 */

const inFlightByKey = new Map();

const oauthExchangeKey = (provider, code) =>
  `${String(provider || "").toLowerCase()}:${String(code || "").trim()}`;

/**
 * Remove OAuth query params from the current URL so remounts cannot re-read the code.
 *
 * @example
 * // URL: /sources/add?code=abc&state=xyz
 * stripOAuthParamsFromUrl();
 * // URL: /sources/add
 */
export const stripOAuthParamsFromUrl = () => {
  if (typeof window === "undefined") {
    return;
  }
  const params = new URLSearchParams(window.location.search);
  const hadOAuthParams =
    params.has("code") ||
    params.has("state") ||
    params.has("scope") ||
    params.has("admin_consent") ||
    params.has("tenant");
  if (!hadOAuthParams) {
    return;
  }
  params.delete("code");
  params.delete("state");
  params.delete("scope");
  params.delete("admin_consent");
  params.delete("tenant");
  const qs = params.toString();
  window.history.replaceState(
    {},
    document.title,
    `${window.location.pathname}${qs ? `?${qs}` : ""}`
  );
};

/**
 * Run exactly one token exchange per (provider, code). Concurrent and follow-up callers
 * share the same settled promise so duplicate client requests never re-hit the provider.
 *
 * @param {string} provider - Integration slug (e.g. "zoom", "gmail")
 * @param {string} code - OAuth authorization code
 * @param {(code: string) => Promise<*>} exchangeFn - Performs the callback POST
 * @returns {Promise<*|null>} Exchange result, or null when code is empty
 *
 * @example
 * const data = await exchangeOAuthCodeOnce("zoom", code, async (authCode) => {
 *   const response = await fetch(`${backend}/externalauth/zoom/callback`, {
 *     method: "POST",
 *     body: JSON.stringify({ code: authCode }),
 *     credentials: "include",
 *   });
 *   if (!response.ok) return null;
 *   return response.json();
 * });
 * // data => { credential_id: "...", message: "..." }
 */
export const exchangeOAuthCodeOnce = async (provider, code, exchangeFn) => {
  const normalizedCode = String(code || "").trim();
  if (!normalizedCode) {
    return null;
  }

  const key = oauthExchangeKey(provider, normalizedCode);
  const existing = inFlightByKey.get(key);
  if (existing) {
    return existing;
  }

  stripOAuthParamsFromUrl();

  const promise = exchangeFn(normalizedCode).catch((error) => {
    inFlightByKey.delete(key);
    throw error;
  });

  inFlightByKey.set(key, promise);
  return promise;
};
